'use client';

import type { InputHTMLAttributes } from 'react';

import { TextInput } from '@eliadmin/core/src/components/nt/forms/controls';
import { describedBy, FormField } from '@eliadmin/core/src/components/nt/forms/form-field';
import { Icon } from '@eliadmin/core/src/components/nt/ui/icon';
import { Spinner } from '@eliadmin/core/src/components/ui/spinner';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';

import type { LookupStatus } from './use-order-lookup';

type FieldProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'id' | 'type'>;

/**
 * „Číslo objednávky“ and „E-mail“ – the two fields that find a guest's order (claim form, order
 * status). Each takes the props of its input: `register('…')` of a form or `{ value, onChange }`.
 */
export function OrderLookupFields({
  prefix,
  identifier,
  email,
  errors,
}: {
  /** Ids of the controls: `<prefix>-identifier`, `<prefix>-email`. */
  prefix: string;
  identifier: FieldProps;
  email: FieldProps;
  errors?: { identifier?: string; email?: string };
}) {
  const { t } = useTranslations();
  const ids = { identifier: `${prefix}-identifier`, email: `${prefix}-email` };

  return (
    <div className="grid gap-4 md:grid-cols-2">
      <FormField id={ids.identifier} label={t('text108')} required error={errors?.identifier}>
        <TextInput
          id={ids.identifier}
          autoComplete="off"
          spellCheck={false}
          aria-required="true"
          aria-describedby={describedBy(ids.identifier, { error: errors?.identifier })}
          invalid={!!errors?.identifier}
          {...identifier}
        />
      </FormField>
      <FormField id={ids.email} label={t('text35')} required error={errors?.email}>
        <TextInput
          id={ids.email}
          type="email"
          inputMode="email"
          autoComplete="email"
          placeholder={t('text259')}
          aria-required="true"
          aria-describedby={describedBy(ids.email, { error: errors?.email })}
          invalid={!!errors?.email}
          {...email}
        />
      </FormField>
    </div>
  );
}

/**
 * What the lookup says while the order is not on the screen: the core hint (the text differs by
 * page – `idleText`), a spinner while asking, the core „order does not exist“ alert. The live region
 * stays mounted, so screen readers announce the change. A found order says nothing here.
 */
export function OrderLookupNotice({
  status,
  idleText,
}: {
  status: LookupStatus;
  idleText: string;
}) {
  const { t } = useTranslations();

  return (
    <div role="status" aria-busy={status === 'pending'} className="empty:hidden">
      {status === 'idle' ? (
        <p className="nt-body-m flex items-start gap-2 text-[#5D655D]">
          <Icon name="info" size={18} className="mt-px shrink-0" />
          {idleText}
        </p>
      ) : null}
      {status === 'pending' ? (
        <div className="flex h-6 items-center">
          <Spinner className="size-5 text-[#127A26]" />
        </div>
      ) : null}
      {status === 'missing' ? (
        <p className="nt-body-m flex items-start gap-2 rounded-xl bg-[#FEF3F2] px-4 py-3 text-[#B42318]">
          <Icon name="warning-circle" size={18} className="mt-px shrink-0 text-[#D92D20]" />
          {t('text2347')}
        </p>
      ) : null}
    </div>
  );
}
